import React, { useState, useEffect } from "react";
import { Modal, Select, Button, Input } from "antd";
import { NodeIndexOutlined, PlusOutlined } from "@ant-design/icons";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { BrandThemeProvider } from "../../../../ui";
import {
  buildInitialValues,
  validationSchema,
} from "./RutasDidacticasForm.form";
import { DidacticRoutesAPI } from "../../../../api";
import styles from "./RutasDidacticasForm.module.scss";

const didacticRoutesAPI = new DidacticRoutesAPI();

export function RutasDidacticasForm({
  accessToken,
  classNumber,
  teacher,
  scene,
  onClose,
  onReload,
  forceCreateMode, // Si es true, fuerza el modo "crear ruta" ignorando rutas
}) {
  const { t } = useTranslation("itineraries");
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  // Rutas del teacher (para el desplegable) si ya existen
  const [routesOptions, setRoutesOptions] = useState([]);
  const [selectedRouteId, setSelectedRouteId] = useState(null);

  // Si el teacher ya tiene itinerarios, mostramos el modo "agregar escena".
  const hasRoutes =
    !forceCreateMode &&
    teacher.rutasDidacticas &&
    teacher.rutasDidacticas.length > 0;

  // Modo de la pantalla: agregar escena a itinerario existente vs crear uno nuevo.
  const isAddMode = hasRoutes;

  useEffect(() => {
    if (hasRoutes) {
      fetchTeacherRoutes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRoutes]);

  const fetchTeacherRoutes = async () => {
    try {
      const result = await didacticRoutesAPI.getRoutesByTeacher(
        accessToken,
        teacher._id,
      );
      // result.routes => array con { _id, nameRute, ... }
      const options = (result.routes || []).map((rt) => ({
        value: rt._id,
        label: rt.nameRute,
      }));
      setRoutesOptions(options);
    } catch (error) {
      console.error("Error al obtener itinerarios del profesor:", error);
    }
  };

  // Formik (solo se usa para crear una ruta nueva)
  const formik = useFormik({
    initialValues: buildInitialValues(classNumber),
    validationSchema: validationSchema(t),
    validateOnChange: false,
    onSubmit: () => {
      setShowConfirm(true);
    },
  });

  /**
   * Se llama cuando el usuario confirma en el modal de confirmacion.
   */
  const handleConfirm = async () => {
    setShowConfirm(false);
    setLoading(true);

    try {
      if (!hasRoutes) {
        // 1) CREAR RUTA NUEVA
        const data = {
          nameRute: formik.values.nameRute,
          description: formik.values.description,
          classnumber: formik.values.classnumber,
          institution_Id: teacher.institution,
        };

        await didacticRoutesAPI.createDidacticRoute(
          accessToken,
          teacher._id,
          teacher.institution._id,
          data,
        );
      } else {
        // 2) AGREGAR ESCENA A ITINERARIO EXISTENTE
        if (!selectedRouteId) {
          throw new Error(t("form.selectItineraryFirst"));
        }

        const routeDetail = await didacticRoutesAPI.getRouteById(
          accessToken,
          selectedRouteId,
        );
        const existingStrings = routeDetail.route.scenesRute.map(
          (item) => item.scene,
        );
        if (!existingStrings.includes(scene.refId)) {
          existingStrings.push(scene.refId);
        }
        const patchData = { scenesRute: existingStrings };

        await didacticRoutesAPI.updateDidacticRoute(
          accessToken,
          selectedRouteId,
          patchData,
        );
      }

      onClose();
      onReload();
      window.location.reload();
    } catch (error) {
      console.error("Error al crear/actualizar el Itinerario:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelConfirm = () => setShowConfirm(false);

  const modalTitle = (
    <div className={styles.header}>
      <span className={styles.headerIcon}>
        <NodeIndexOutlined />
      </span>
      <div className={styles.headerText}>
        <span className={styles.headerTitle}>
          {isAddMode
            ? t("form.modalHeaderAddToItinerary")
            : t("form.modalHeaderCreateNew")}
        </span>
        {isAddMode && scene?.title && (
          <span className={styles.headerSub}>{scene.title}</span>
        )}
      </div>
    </div>
  );

  const footer = isAddMode
    ? [
        <Button key="cancel" onClick={onClose}>
          {t("form.closeButton")}
        </Button>,
        <Button
          key="add"
          type="primary"
          icon={<PlusOutlined />}
          loading={loading}
          disabled={!selectedRouteId}
          onClick={() => setShowConfirm(true)}
        >
          {t("form.addSceneButton")}
        </Button>,
      ]
    : [
        <Button key="cancel" onClick={onClose}>
          {t("form.closeButton")}
        </Button>,
        <Button
          key="create"
          type="primary"
          icon={<PlusOutlined />}
          loading={loading}
          onClick={formik.handleSubmit}
        >
          {t("form.createButton")}
        </Button>,
      ];

  return (
    <BrandThemeProvider>
      <Modal
        open
        onCancel={onClose}
        centered
        width={520}
        title={modalTitle}
        footer={footer}
        styles={{ body: { paddingTop: 8 } }}
      >
        {isAddMode ? (
          <div className={styles.body}>
            <p className={styles.lead}>{t("form.addSceneSelectItinerary")}</p>
            <Select
              className={styles.select}
              size="large"
              placeholder={t("form.sceneSelectPlaceholder")}
              options={routesOptions}
              value={selectedRouteId}
              onChange={(value) => setSelectedRouteId(value)}
              showSearch
              optionFilterProp="label"
              notFoundContent={t("list.noItineraries")}
            />
          </div>
        ) : (
          <div className={styles.body}>
            <div className={styles.field}>
              <label className={styles.label}>{t("form.nameLabel")}</label>
              <Input
                size="large"
                name="nameRute"
                value={formik.values.nameRute}
                onChange={formik.handleChange}
                placeholder={t("form.namePlaceholder")}
                status={formik.errors.nameRute ? "error" : ""}
              />
              {formik.errors.nameRute && (
                <span className={styles.error}>{formik.errors.nameRute}</span>
              )}
            </div>

            <div className={styles.field}>
              <label className={styles.label}>
                {t("form.descriptionLabel")}
              </label>
              <Input.TextArea
                name="description"
                value={formik.values.description}
                onChange={formik.handleChange}
                placeholder={t("form.descriptionPlaceholder")}
                rows={3}
                status={formik.errors.description ? "error" : ""}
              />
              {formik.errors.description && (
                <span className={styles.error}>
                  {formik.errors.description}
                </span>
              )}
            </div>

            <div className={styles.field}>
              <label className={styles.label}>{t("form.classLabel")}</label>
              <Input
                size="large"
                name="classnumber"
                value={formik.values.classnumber}
                onChange={formik.handleChange}
                placeholder={t("form.classPlaceholder")}
                status={formik.errors.classnumber ? "error" : ""}
              />
              {formik.errors.classnumber && (
                <span className={styles.error}>
                  {formik.errors.classnumber}
                </span>
              )}
            </div>

            {!formik.values.classnumber && (
              <p className={styles.warn}>{t("form.noClassWarning")}</p>
            )}
          </div>
        )}
      </Modal>

      <Modal
        open={showConfirm}
        onCancel={handleCancelConfirm}
        onOk={handleConfirm}
        confirmLoading={loading}
        centered
        width={440}
        okText={t("form.confirmOk")}
        cancelText={t("form.confirmCancel")}
        title={
          isAddMode
            ? t("form.modalHeaderAddToItinerary")
            : t("form.modalHeaderCreateNew")
        }
      >
        <p className={styles.confirmText}>
          {isAddMode
            ? t("form.confirmAddSceneContent", { title: scene?.title })
            : t("form.confirmCreateContent", { name: formik.values.nameRute })}
        </p>
      </Modal>
    </BrandThemeProvider>
  );
}
